import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import FilterBar from "../../components/cateringPackages/FilterBar";
import PackageGrid from "../../components/cateringPackages/PackageGrid";
import SummarySection from "../../components/cateringPackages/SummarySection";
import { Menu } from "../../components/menu";
import CateringOrderSummary from "../../components/cateringPackages/CateringOrderSummary";
import { getRestaurantItemsCategoriesAsync, getCateringPackagesAsync, getCateringPackageDetailAsync } from "../../redux/features/restaurants/restaurantThunks";
import { getCateringCartList } from "../../api/get/getCateringCartList";
import { transformBackendCartToFrontend } from "../../utils/catering";
import { addCateringCartPackage, removeCateringCartPackage, setCateringCartFilters, setCateringCheckoutCart, setCateringCartIds } from "../../redux/features/restaurants/restaurantSlice";
import { selectCateringPackages, selectCateringRestaurantId, selectCartRestaurantDetails, selectCateringCartPackages, selectCateringCartFilters, selectCateringCheckoutCart, selectCateringCartIds } from "../../redux/features/restaurants/restaurantSelectors";
import { isDateBlocked, getTodayDate } from "../../utils/timeSlots";

const CateringPackages = () => {
  const dispatch = useDispatch();
  const cateringPackages = useSelector(selectCateringPackages);
  const cateringRestaurantId = useSelector(selectCateringRestaurantId);
  const cartRestaurantDetails = useSelector(selectCartRestaurantDetails);
  const cateringCartPackages = useSelector(selectCateringCartPackages);
  const filters = useSelector(selectCateringCartFilters);
  const checkoutCart = useSelector(selectCateringCheckoutCart);
  // const cateringCartIds = useSelector(selectCateringCartIds);

  // State for packages (filters now come from Redux)
  const [selectedPackages, setSelectedPackages] = useState([]);
  const [loading, setLoading] = useState(true);

  // Function to check and repopulate cart from backend
  // const checkAndRepopulateCart = useCallback(async () => {
  //   try {
  //     console.log('Checking cart data from backend...');
  //     const cartResponse = await getCateringCartList();
      
  //     if (cartResponse && cartResponse.data && Array.isArray(cartResponse.data) && cartResponse.data.length > 0) {
  //       // Transform backend data to frontend structure
  //       const transformedCart = transformBackendCartToFrontend(cartResponse.data);
        
  //       if (transformedCart.length > 0) {
  //         // Update Redux with the cart data
  //         dispatch(setCateringCheckoutCart(transformedCart));
          
  //         // Extract cart IDs from the response
  //         const cartIds = cartResponse.data.map(item => item.cart_id || item.id).filter(Boolean);
  //         if (cartIds.length > 0) {
  //           dispatch(setCateringCartIds({ cart_id: cartIds[0] })); // Use first cart ID
  //         }
          
  //         console.log('Cart repopulated from backend:', transformedCart);
  //       }
  //     } else {
  //       console.log('No cart data found in backend');
  //     }
  //   } catch (error) {
  //     console.error('Error checking/repopulating cart:', error);
  //   }
  // }, [dispatch]);



  // Function to map API data to UI format
  const mapCateringPackagesToUIFormat = (apiPackages, selectedDate = null) => {
    if (!apiPackages || !Array.isArray(apiPackages)) return [];

    const today = getTodayDate();

    return apiPackages
      .map(pkg => {
        // Check if package is disabled due to publish status
        let isDisabled = pkg.publish !== 'Publish';
        
        // If a date is selected, also check availability for that date
        if (selectedDate && !isDisabled) {
          const packageData = {
            cutt_off_time_in_hours: pkg.cutt_off_time_in_hours,
            unavailability_schedule: pkg.unavailability_schedule || []
          };
          
          // Check if the selected date is blocked for this specific package
          isDisabled = isDateBlocked(selectedDate, today, packageData);
        }

        return {
          id: pkg.id,
          restaurant_id: pkg.restaurant_id, // Add restaurant_id for restaurant validation
          image: pkg.image || 'https://images.unsplash.com/photo-1506744038136-46273834b3fb', // Use image or fallback
          title: pkg.name_en || pkg.name_ar || 'Package',
          description: pkg.description_en || pkg.description_ar || '',
          basePrice: pkg.pricing_tiers && pkg.pricing_tiers.length > 0 ? pkg.pricing_tiers[0].price : 0, // Use first pricing tier
          isDisabled: isDisabled, // Disabled if not published OR if date is blocked
          displayOrder: pkg.display_order || 0,
          setupTime: pkg.setup_time,
          serviceDuration: pkg.service_duration,
          servePeople: `${pkg.min_pax} - ${pkg.max_pax}`, // Combine min and max pax
          presentation: pkg.presentation_en || pkg.presentation_ar,
          food: pkg.food_en || pkg.food_ar,
          specialNotes: pkg.special_notes_en || pkg.special_notes_ar,
          categoryName: pkg.categories && pkg.categories.length > 0 ? pkg.categories[0].name_en || pkg.categories[0].name_ar : '',
          isPopular: false, // Not available in new structure
          isTopPick: false, // Not available in new structure
          isNew: false, // Not available in new structure
          paxLimit: pkg.pricing_tiers || [], // Use pricing_tiers for pax limits
          items: pkg.categories || [], // Use categories as items
          minPax: pkg.min_pax,
          maxPax: pkg.max_pax,
          pricingTiers: pkg.pricing_tiers || [],
          unavailabilitySchedule: pkg.unavailability_schedule || [],
          cuttOffTimeInHours: pkg.cutt_off_time_in_hours,
          electricOutlet: pkg.electric_outlet,
          waterSupply: pkg.water_supply,
        };
      })
      .sort((a, b) => a.displayOrder - b.displayOrder); // Sort by display_order
  };

  // Get processed packages from Redux data
  const packages = mapCateringPackagesToUIFormat(cateringPackages, filters?.date);
  const summary = [
    { name: 'Melenzane', total: 20, approved: 16, tentative: 4 },
    { name: 'Masgouf', total: 10, approved: 6, tentative: 4 },
    { name: 'MMM', total: 10, approved: 6, tentative: 4 },
    { name: 'The Grove', total: 10, approved: 6, tentative: 4 },
    { name: 'CAFAÐ', total: 10, approved: 6, tentative: 4 },
    { name: 'Seazen', total: 10, approved: 6, tentative: 4 },
    { name: "Surf 'n' Fries", total: 10, approved: 6, tentative: 4 },
    { name: 'Recco', total: 10, approved: 6, tentative: 4 },
  ];

  const handlePackageSelect = (packageToSelect) => {
    if (packageToSelect) {
      // Check if package is already in checkout cart
      const isInCheckoutCart = checkoutCart.some(pkg => pkg.id === packageToSelect.id);

      if (isInCheckoutCart) {
        // Package is already in checkout cart, don't remove it
        // Just update local selected packages for UI
        setSelectedPackages(prevSelected =>
          prevSelected.some(pkg => pkg.id === packageToSelect.id)
            ? prevSelected.filter(pkg => pkg.id !== packageToSelect.id)
            : [...prevSelected, packageToSelect]
        );
      } else {
        // Package is not in checkout cart, proceed normally
        // Update local selected packages for UI
        setSelectedPackages(prevSelected =>
          prevSelected.some(pkg => pkg.id === packageToSelect.id)
            ? prevSelected.filter(pkg => pkg.id !== packageToSelect.id)
            : [...prevSelected, packageToSelect]
        );
      }
    }
  };

  // Fetch catering data on mount and when restaurant changes
  useEffect(() => {
    const fetchCateringData = async () => {
      // Use restaurant ID from cart, or default to 1 if not available
      const restaurantId = cartRestaurantDetails?.restaurantId || cartRestaurantDetails?.restaurant_id || 1;

      try {
        console.log("Fetching catering data for restaurant:", restaurantId);
        console.log("Current catering data is for restaurant:", cateringRestaurantId);

        // Check if we need to fetch data for a different restaurant
        const isRestaurantDifferent = cateringRestaurantId !== restaurantId;

        // Always fetch catering packages to get the latest information
        // This ensures we have up-to-date package data including availability schedules
        // and any changes made to packages since the last visit
        if (isRestaurantDifferent) {
          console.log("Restaurant changed, refetching catering packages...");
        } else {
          console.log("Fetching latest catering packages to ensure fresh data...");
        }
        
        await dispatch(getCateringPackagesAsync(restaurantId, "", "en", 3));
        setLoading(false);
      } catch (error) {
        console.error("Failed to fetch catering data:", error);
        setLoading(false);
      }
    };

    // Set loading to true when starting to fetch
    setLoading(true);
    fetchCateringData();
  }, [dispatch, cartRestaurantDetails?.restaurantId, cateringRestaurantId]);

  // // Check and repopulate cart from backend on mount
  // useEffect(() => {
  //   checkAndRepopulateCart();
  // }, [checkAndRepopulateCart]); // Runs when checkAndRepopulateCart changes (which should be stable)

  // Log catering data when it changes
  useEffect(() => {
    console.log("Catering packages state:", cateringPackages);
    console.log("Cart restaurant details:", cartRestaurantDetails);
    console.log("Catering restaurant ID:", cateringRestaurantId);
    console.log("Filters:", filters);
    
    if (cateringPackages) {
      console.log("Catering packages loaded:", cateringPackages);
      console.log("Mapped packages for UI:", mapCateringPackagesToUIFormat(cateringPackages, filters?.date));
    } else {
      console.log("No catering packages available");
    }
  }, [cateringPackages, cartRestaurantDetails, cateringRestaurantId, filters]);

  // Auto-select first package when packages data is loaded (single package restriction)
  React.useEffect(() => {
    if (packages && packages.length > 0 && !loading) {
      const packageToSelect = packages.find(pkg => !pkg.isDisabled);
      setSelectedPackages(packageToSelect ? [packageToSelect] : []);
    }
  }, [loading]);

  if (loading || !cateringPackages) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="spinner-border animate-spin border-t-4 border-b-4 border-gray-600 w-16 h-16 rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <FilterBar />
      <div className="flex flex-1 flex-row overflow-hidden">
        <div className="w-2/3 p-6 flex flex-col flex-1 overflow-y-auto">
          <div className="flex-grow">
            <PackageGrid packages={packages} onPackageSelect={handlePackageSelect} filters={filters} />
          </div>
          {/* <div className="w-full">
            <SummarySection summary={summary} />
          </div> */}
        </div>
        <div className="w-1/4 border-l border-gray-200 bg-[#FBFBFB] flex-shrink-0 flex flex-col">
          {Array.isArray(checkoutCart) && checkoutCart.length > 0 && (() => {
            // Filter checkout cart to only show packages from current restaurant
            const currentRestaurantId = cartRestaurantDetails?.restaurantId;
            const filteredCheckoutCart = checkoutCart.filter(pkg => {
              // If package has restaurant_id, filter by it
              if (pkg.restaurant_id && currentRestaurantId) {
                return pkg.restaurant_id === currentRestaurantId;
              }
              // If no restaurant_id in package, include it (backward compatibility)
              return true;
            });
            
            return filteredCheckoutCart.length > 0 ? (
              <CateringOrderSummary selectedPackages={filteredCheckoutCart} />
            ) : null;
          })()}
        </div>
      </div>
    </div>
  );
};

export default CateringPackages; 
